import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cmsUrl } from "@kozien/shared";
import { useDealProducts } from "@/models/deal";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import { useProductEligibility, useProducts } from "@/models/product";
import { resolveSelectionColors } from "@/models/color/resolveSelectionColors";
import {
    findConflictingSameCategoryCombo,
    getAllConfiguredCombos,
    useSelectionStore,
} from "@/store/selection";
import {
    WindowTile,
    type WindowTileConfiguration,
} from "@/components/molecules/WindowTile/WindowTile";
import { ReplaceProductDialog } from "@/components/molecules/ReplaceProductDialog/ReplaceProductDialog";
import { Skeleton } from "@/components/atoms/Skeleton/Skeleton";
import { filterEligibleWindows } from "@/lib/filterEligibleWindows";
import type { ProductType } from "@/models/product";

type ReplaceTarget = {
    windowId: string;
    fromCategory: ProductType;
    fromTypeId: string;
};

const WINDOW_STEPS_LENGTH = Array.from({ length: 6 });

export function WindowsStep() {
    const { t } = useTranslation();
    const { products, loading } = useDealProducts();
    const productIds = useMemo(
        () => products.map((p) => p.productId),
        [products],
    );
    const { eligibilityByProductId, loading: eligibilityLoading } =
        useProductEligibility(productIds);
    const { data: pageContent } = useSunProtectionPageContent();
    const activeCategory = useSelectionStore((s) => s.selectedCategory);
    const cmsProducts = useProducts(activeCategory).products;
    const selectedProductIds = useSelectionStore((s) => s.selectedProductIds);
    const toggleProductId = useSelectionStore((s) => s.toggleProductId);
    const replaceProductOnWindow = useSelectionStore(
        (s) => s.replaceProductOnWindow,
    );
    const cachedByCombo = useSelectionStore((s) => s.cachedByCombo);
    const activeTypeId = useSelectionStore((s) => s.selectedTypeId);
    const activeWindowSelections = useSelectionStore((s) => s.windowSelections);

    const [replaceTarget, setReplaceTarget] = useState<ReplaceTarget | null>(
        null,
    );

    const configurationsByWindow = useMemo(() => {
        const combos = getAllConfiguredCombos({
            selectedCategory: activeCategory,
            selectedTypeId: activeTypeId,
            selectedProductIds,
            windowSelections: activeWindowSelections,
            cachedByCombo,
            dealProducts: [],
            lastTypeIdByCategory: {},
        }).filter((c) => c.category === activeCategory);
        const map: Record<string, WindowTileConfiguration[]> = {};
        for (const combo of combos) {
            const cmsProduct = cmsProducts.find((p) => p.id === combo.typeId);
            for (const windowId of combo.selectedProductIds) {
                const sel = combo.windowSelections[windowId];
                if (!sel) continue;
                const list = map[windowId] ?? [];
                list.push({
                    id: `${combo.category}:${combo.typeId}`,
                    ...resolveSelectionColors(cmsProduct, sel, combo.typeId),
                });
                map[windowId] = list;
            }
        }
        return map;
    }, [
        activeCategory,
        activeTypeId,
        selectedProductIds,
        activeWindowSelections,
        cachedByCombo,
        cmsProducts,
    ]);

    function handleToggle(windowId: string) {
        // Deselecting an already-active window is never a conflict.
        if (selectedProductIds.includes(windowId)) {
            toggleProductId(windowId);
            return;
        }
        const conflict = findConflictingSameCategoryCombo(
            useSelectionStore.getState(),
            windowId,
        );
        if (conflict) {
            setReplaceTarget({
                windowId,
                fromCategory: conflict.category,
                fromTypeId: conflict.typeId,
            });
            return;
        }
        toggleProductId(windowId);
    }

    const title =
        pageContent?.productSelectionStep?.title ?? t("windowsStep.title");
    const subtitle =
        pageContent?.productSelectionStep?.content ?? t("windowsStep.subtitle");

    const eligibleProducts = useMemo(
        () =>
            filterEligibleWindows(
                products,
                eligibilityByProductId,
                activeCategory,
            ),
        [products, eligibilityByProductId, activeCategory],
    );

    if (loading || eligibilityLoading) {
        return (
            <div className="flex flex-col gap-4">
                <Skeleton className="h-8 w-64 mx-auto" />
                <Skeleton className="h-4 w-80 mx-auto" />
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                    {WINDOW_STEPS_LENGTH.map((_, i) => (
                        <Skeleton
                            key={i}
                            className="aspect-square w-full rounded-xl"
                        />
                    ))}
                </div>
            </div>
        );
    }

    const grouped = eligibleProducts.reduce<
        Record<string, typeof eligibleProducts>
    >((acc, product) => {
        const loc = product.locationName;
        if (!acc[loc]) acc[loc] = [];
        acc[loc].push(product);
        return acc;
    }, {});

    const locationNames = Object.keys(grouped);

    return (
        <div className="flex flex-col gap-8 pb-8 sm:pb-0">
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 leading-tight">
                    {title}
                </h1>
                <p className="mt-3 text-base text-gray-500 max-w-lg mx-auto">
                    {subtitle}
                </p>
            </div>

            {locationNames.length === 0 ? (
                <p className="text-center text-gray-400 py-12">
                    {t(
                        products.length === 0
                            ? "windowsStep.noProducts"
                            : "windowsStep.noEligibleProducts",
                    )}
                </p>
            ) : (
                <div className="sm:bg-white rounded-2xl sm:shadow-sm overflow-hidden">
                    {locationNames.map((locationName, groupIndex) => (
                        <div key={locationName}>
                            {groupIndex > 0 && (
                                <div className="border-t border-gray-100" />
                            )}
                            <div className="sm:px-7 pt-6 pb-3">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {locationName}
                                </h2>
                            </div>
                            <div className="grid grid-cols-2 gap-4 sm:px-5 pb-5 md:grid-cols-3">
                                {grouped[locationName].map((product) => {
                                    return (
                                        <WindowTile
                                            key={product.id}
                                            imageUrl={
                                                product.previewImage?.url
                                                    ? cmsUrl(
                                                          product.previewImage
                                                              .url,
                                                          import.meta.env
                                                              .VITE_CMS_BASE_ENDPOINT,
                                                      )
                                                    : null
                                            }
                                            selected={selectedProductIds.includes(
                                                product.id,
                                            )}
                                            onToggle={() =>
                                                handleToggle(product.id)
                                            }
                                            configurations={
                                                configurationsByWindow[
                                                    product.id
                                                ]
                                            }
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ReplaceProductDialog
                open={replaceTarget !== null}
                category={replaceTarget?.fromCategory ?? "SUN_PROTECTION"}
                onConfirm={() => {
                    if (!replaceTarget) return;
                    replaceProductOnWindow(
                        replaceTarget.windowId,
                        replaceTarget.fromCategory,
                        replaceTarget.fromTypeId,
                    );
                    setReplaceTarget(null);
                }}
                onClose={() => setReplaceTarget(null)}
            />
        </div>
    );
}
